import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, PackageCheck } from "lucide-react";
import { API, BACKEND } from "./shared";

// Design Library — buyer's permanent archive of every delivered design brief.
// Lists cards per brief with file count, delivery date, agency name, and
// one-click "Download all (ZIP)" + "Download copyright certificate (PDF)".
// Also serves as the agency's view of what they have shipped.
export function DesignLibrary({ role }) {
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/design/library`);
      setItems(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load the design library");
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }); }
    catch { return "—"; }
  };

  const filtered = (items || []).filter(i => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (i.title || "").toLowerCase().includes(q)
      || (i.agency_name || "").toLowerCase().includes(q)
      || (i.buyer_name || "").toLowerCase().includes(q)
      || (i.category || "").toLowerCase().includes(q);
  });

  const headers = (() => {
    const t = axios.defaults.headers.common["Authorization"];
    return t ? { Authorization: t } : {};
  })();

  const downloadBlob = async (url, filename) => {
    try {
      const res = await axios.get(url, { responseType: "blob", headers });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { window.URL.revokeObjectURL(link.href); link.remove(); }, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  return (
    <section className="content" data-testid="design-library">
      <div className="section-heading">
        <div>
          <p className="eyebrow">YOUR ARCHIVE</p>
          <h2>Design library</h2>
          <p className="subhead">
            {role === "buyer"
              ? "Every brief your awarded agencies have delivered — download source files any time, grab a copyright certificate for your records, and reuse artwork across SKUs. You hold full copyright per NDA Clause 4."
              : role === "agency"
              ? "A log of every brief you have delivered on GoPrintPack. Buyers download from here — your portfolio reach stays intact."
              : "All delivered design briefs across the platform."}
          </p>
        </div>
      </div>

      <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-end",marginBottom:16}}>
        <label style={{display:"flex",flexDirection:"column",gap:4,fontSize:11,color:"#6B7280",fontWeight:600,letterSpacing:"0.05em",textTransform:"uppercase",flex:"1 1 320px"}}>
          Search
          <input
            data-testid="design-library-search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Brief title, agency, buyer, category…"
            style={{padding:"8px 10px",border:"1px solid #d1d5db",borderRadius:8,fontSize:13,background:"#fff",color:"#111827",fontWeight:500,textTransform:"none",letterSpacing:"normal"}}
          />
        </label>
        <p data-testid="design-library-count" style={{margin:0,fontSize:12,color:"#6B7280"}}>
          Showing <strong style={{color:"#111827"}}>{filtered.length}</strong> delivered brief{filtered.length === 1 ? "" : "s"}
        </p>
      </div>

      {err && <div style={{background:"#FEF2F2",border:"1px solid #FECACA",color:"#991B1B",padding:"8px 12px",borderRadius:8,fontSize:12,marginBottom:12}}>{err}</div>}

      {items === null ? (
        <p style={{color:"#6B7280",fontSize:13}}>Loading your library…</p>
      ) : filtered.length === 0 ? (
        <div style={{padding:"40px 24px",background:"#F9FAFB",border:"1px dashed #D1D5DB",borderRadius:12,textAlign:"center",color:"#6B7280",fontSize:14}}>
          <PackageCheck size={32} strokeWidth={1.4} style={{opacity:0.5}}/>
          <p style={{margin:"10px 0 2px",fontWeight:600,color:"#111827"}}>No delivered briefs yet.</p>
          <p style={{margin:0,fontSize:12}}>Delivered artwork will appear here with downloadable source files and a copyright certificate.</p>
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(340px, 1fr))",gap:16}} data-testid="design-library-grid">
          {filtered.map(i => (
            <div key={i.brief_id} data-testid={`library-card-${i.brief_id}`} style={{background:"#fff",border:"1px solid #E5E7EB",borderRadius:14,display:"flex",flexDirection:"column",boxShadow:"0 10px 25px -20px rgba(15,23,42,0.4)",overflow:"hidden"}}>
              <div style={{position:"relative",width:"100%",paddingTop:"62.5%",background:"#F3F4F6",overflow:"hidden"}}>
                <img
                  src={`${BACKEND}${i.thumbnail_url}`}
                  alt={`${i.title} preview`}
                  data-testid={`library-thumb-${i.brief_id}`}
                  loading="lazy"
                  style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",display:"block"}}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              <div style={{padding:"16px 18px 18px",display:"flex",flexDirection:"column",gap:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                  <span style={{background:"#DCFCE7",color:"#065F46",padding:"3px 10px",borderRadius:999,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em"}}>Delivered</span>
                  <span style={{fontSize:11,color:"#6B7280"}}>{fmtDate(i.delivered_at)}</span>
                </div>
                <h3 style={{margin:0,fontSize:16,color:"#111827",fontFamily:"'Fraunces', serif",fontWeight:600,lineHeight:1.25}}>{i.title}</h3>
                <p style={{margin:0,fontSize:12,color:"#6B7280"}}>
                  {role === "agency" ? <>Buyer: <b style={{color:"#111827"}}>{i.buyer_name || "—"}</b></> : <>Agency: <b style={{color:"#111827"}}>{i.agency_name || "—"}</b></>}
                  {i.category ? <> · {i.category.replace(/_/g, " ")}</> : null}
                </p>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:8,fontSize:12,color:"#374151"}}>
                  <FileText size={14}/>
                  <span><b>{i.file_count}</b> source file{i.file_count === 1 ? "" : "s"}</span>
                  {i.locked && <span style={{marginLeft:"auto",color:"#92400E",fontWeight:600}}>Locked · NDA pending</span>}
                </div>
                {i.locked ? (
                  <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",color:"#92400E",padding:"8px 12px",borderRadius:8,fontSize:12}}>
                    Downloads unlock once both parties sign the mutual NDA. Open the brief to sign.
                  </div>
                ) : (
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:4}}>
                    <button
                      type="button"
                      data-testid={`library-zip-${i.brief_id}`}
                      onClick={() => downloadBlob(`${API}/design/briefs/${i.brief_id}/sources.zip`, `artwork-${i.brief_id}.zip`)}
                      style={{background:"#111827",color:"#FACC15",border:"none",padding:"8px 14px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}}
                    ><PackageCheck size={13}/> Download all (ZIP)</button>
                    <button
                      type="button"
                      data-testid={`library-cert-${i.brief_id}`}
                      onClick={() => downloadBlob(`${API}/design/briefs/${i.brief_id}/copyright-certificate.pdf`, `copyright-certificate-${i.brief_id}.pdf`)}
                      style={{background:"#F3F4F6",color:"#111827",border:"1px solid #D1D5DB",padding:"8px 14px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6}}
                    ><FileText size={13}/> Copyright PDF</button>
                  </div>
                )}
                {i.source_file_urls && i.source_file_urls.length > 0 && !i.locked && (
                  <details style={{marginTop:4}} data-testid={`library-files-${i.brief_id}`}>
                    <summary style={{cursor:"pointer",fontSize:11,color:"#6B7280",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.08em"}}>Individual files</summary>
                    <ul style={{listStyle:"none",padding:0,margin:"8px 0 0",display:"grid",gap:6}}>
                      {i.source_file_urls.map((u, idx) => (
                        <li key={idx} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 10px",background:"#F9FAFB",border:"1px solid #E5E7EB",borderRadius:6,fontSize:12}}>
                          <span style={{color:"#111827",fontFamily:"'DM Mono', monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.split("/").pop()}</span>
                          <a href={u} target="_blank" rel="noreferrer" style={{color:"#2563EB",fontWeight:600,textDecoration:"none"}}>Open →</a>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
